import { useState } from 'react';
import type { OnboardingNote } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { formatDateTime } from '../../utils/date.js';
import { RichTextContent, RichTextEditor } from '../../components/ui/RichText.js';
import {
  useCreateOnboardingNote,
  useDeleteOnboardingNote,
  useOnboardingNotes,
  useUpdateOnboardingNote,
} from './api.js';

interface OnboardingNotesSectionProps {
  onboardingId: string;
  canEdit: boolean;
}

const EMPTY_BODY = '<p></p>';

function isEmptyBody(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').trim() === '';
}

function NoteCard({
  note,
  canEdit,
  onSave,
  isSaving,
  onDelete,
  isDeleting,
}: {
  note: OnboardingNote;
  canEdit: boolean;
  onSave: (id: string, body: string, done: () => void) => void;
  isSaving: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}): JSX.Element {
  const [confirm, setConfirm] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          <span className="font-medium text-slate-700">{note.authorName}</span> ·{' '}
          {formatDateTime(note.createdAt)}
          {note.updatedAt ? (
            <span className="ml-1 italic">
              (edited{note.editedByName ? ` by ${note.editedByName}` : ''} ·{' '}
              {formatDateTime(note.updatedAt)})
            </span>
          ) : null}
        </div>
        {canEdit && !isEditing ? (
          confirm ? (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                onClick={() => onDelete(note.id)}
                isLoading={isDeleting}
              >
                Confirm
              </Button>
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs"
                onClick={() => setConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs"
                onClick={() => setDraft(note.body)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirm(true)}
              >
                Delete
              </Button>
            </div>
          )
        ) : null}
      </div>
      {isEditing ? (
        <div className="flex flex-col gap-2">
          <RichTextEditor value={draft} onChange={setDraft} placeholder="Edit note…" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              isLoading={isSaving}
              disabled={isEmptyBody(draft)}
              onClick={() => onSave(note.id, draft, () => setDraft(null))}
            >
              Save note
            </Button>
          </div>
        </div>
      ) : (
        <RichTextContent html={note.body} />
      )}
    </div>
  );
}

export function OnboardingNotesSection({
  onboardingId,
  canEdit,
}: OnboardingNotesSectionProps): JSX.Element {
  const [body, setBody] = useState(EMPTY_BODY);

  const notesQuery = useOnboardingNotes(onboardingId);
  const createMutation = useCreateOnboardingNote();
  const updateMutation = useUpdateOnboardingNote();
  const deleteMutation = useDeleteOnboardingNote();

  const notes = notesQuery.data ?? [];

  const generalError = createMutation.isError
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Failed to add note.'
    : updateMutation.isError
      ? updateMutation.error instanceof ApiError
        ? updateMutation.error.message
        : 'Failed to save note.'
      : null;

  const handleAdd = (): void => {
    if (isEmptyBody(body)) return;
    createMutation.mutate({ onboardingId, body }, { onSuccess: () => setBody(EMPTY_BODY) });
  };

  const handleSave = (noteId: string, nextBody: string, done: () => void): void => {
    if (isEmptyBody(nextBody)) return;
    updateMutation.mutate({ onboardingId, noteId, body: nextBody }, { onSuccess: done });
  };

  const handleDelete = (noteId: string): void => {
    deleteMutation.mutate({ onboardingId, noteId });
  };

  return (
    <div className="flex flex-col gap-4">
      {canEdit ? (
        <div className="flex flex-col gap-2">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}
          <RichTextEditor value={body} onChange={setBody} placeholder="Add a note…" />
          <div className="flex justify-end">
            <Button
              onClick={handleAdd}
              isLoading={createMutation.isPending}
              disabled={isEmptyBody(body)}
            >
              Add note
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {notesQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-slate-500">No notes yet.</p>
        ) : (
          notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              canEdit={canEdit}
              onSave={handleSave}
              isSaving={updateMutation.isPending}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}
